import _ from "lodash";
import React from "react";
import ThemedModal from "../../../components/utilities/ThemedModal";
import { Button } from "../../../components";
import { Modal } from "react-bootstrap";
import { ModalConfirmation } from "../../../types/DefModalOptions";
import { Controller, useForm } from "react-hook-form";
import { FormSelect } from "../../../components/forms";
import { GenericUserSelect } from "../../../components/forms/selects";
import { UserCalendarSelect } from "../../../components/forms/selects/UserCalendarSelect";
import { UserMailboxSelect } from "../../../components/forms/selects/UserMailboxSelect";
import { useAutoAnimate } from "@formkit/auto-animate/react";
import { confirmable, createConfirmation } from "../../../hooks/DialogMountPoint";
import { SubscriptionTypeOptions } from "../constants";
import { useLazyFetchMailFoldersListQuery } from "../../../services/graph";

export type CreateSubscriptionForm = {
    userId: string
    type: number
    resourceId?: string | null
};

export type CreateSubscriptionModalProps = ModalConfirmation<CreateSubscriptionForm>;
const CreateSubscriptionModal = ({
    backdrop = 'static',
    cancel,
    centered = true,
    defaultValues,
    dialogId,
    dismiss,
    proceed,
    show,
    size = "lg",
    ...rest
}: Readonly<CreateSubscriptionModalProps>) => {
    const [bodyAnimation] = useAutoAnimate();
    const { control, formState: { errors }, handleSubmit, reset, setValue, watch } = useForm<CreateSubscriptionForm>();
    const selectedUserId = watch('userId');
    const selectedType = watch('type');
    const isMailboxSubscription = selectedType === 0;
    const isCalendarSubscription = selectedType === 1;

    const onClose = () => cancel!();

    const onSubmit = (form: CreateSubscriptionForm) => proceed!(form);

    React.useEffect(() => {
        if (_.isEmpty(defaultValues)) {
            return;
        }
        reset({ ...defaultValues });
    }, [ defaultValues ]);

    return (
        <Modal backdrop={backdrop} centered={centered} size={size} show={show} onHide={onClose} {...rest}>
            <ThemedModal.Header>
                <Modal.Title>Create a new Subscription</Modal.Title>
            </ThemedModal.Header>
            <form onSubmit={handleSubmit(onSubmit)}>
                <Modal.Body ref={bodyAnimation}>
                    <div className="row gy-2">
                        <div className="col-12">
                            <Controller
                                name="type"
                                control={control}
                                rules={{ required: 'Type of subscription must be selected' }}
                                render={({ field: { ref, value, onChange, ...rest } }) => (
                                    <FormSelect
                                        label="Subscription Type"
                                        options={SubscriptionTypeOptions}
                                        defaultValue={value}
                                        disableAnimations={true}
                                        errorMessage={errors?.type?.message}
                                        errorAllowRetry={false}
                                        onChange={(values: any) => {
                                            setValue('type', values.value, { shouldDirty: true });
                                            setValue('resourceId', null, { shouldDirty: true });
                                        }}
                                        {...rest}
                                    />
                                )}
                            />
                        </div>
                        <div className="col-6">
                            <Controller
                                name="userId"
                                control={control}
                                rules={{ required: 'A user must be selected' }}
                                render={({ field: { ref, value, onChange, ...rest } }) => (
                                    <GenericUserSelect
                                        label="User"
                                        defaultValue={value}
                                        disableAnimations={true}
                                        errorMessage={errors?.userId?.message}
                                        errorAllowRetry={false}
                                        onChange={(values: any) => {
                                            setValue('userId', values.value, { shouldDirty: true });
                                            setValue('resourceId', null, { shouldDirty: true });
                                        }}
                                        {...rest}
                                    />
                                )}
                            />
                        </div>
                        {selectedUserId && isMailboxSubscription && (
                            <div className="col-6">
                                <Controller
                                    name="resourceId"
                                    control={control}
                                    render={({ field: { ref, value, onChange, ...rest } }) => (
                                        <UserMailboxSelect
                                            query={useLazyFetchMailFoldersListQuery}
                                            userId={selectedUserId}
                                            value={value ?? null}
                                            onChange={(values: any) => setValue('resourceId', values.value, { shouldDirty: true })}
                                            {...rest}
                                        />
                                    )}
                                />
                            </div>
                        )}
                        {selectedUserId && isCalendarSubscription && (
                            <div className="col-6">
                                <Controller
                                    name="resourceId"
                                    control={control}
                                    render={({ field: { ref, value, onChange, ...rest } }) => (
                                        <UserCalendarSelect
                                            userId={selectedUserId}
                                            value={value ?? null}
                                            onChange={(values: any) => setValue('resourceId', values.value, { shouldDirty: true })}
                                            {...rest}
                                        />
                                    )}
                                />
                            </div>
                        )}
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="success" type="submit">Create Subscription</Button>
                    <Button variant="light" onClick={onClose}>Cancel</Button>
                </Modal.Footer>
            </form>
        </Modal>
    );
};

export function openCreateSubscriptionModal(opts: CreateSubscriptionForm = { userId: '', type: 0, resourceId: null }) {
    return createConfirmation<CreateSubscriptionModalProps, CreateSubscriptionForm>(confirmable(CreateSubscriptionModal))({
        dialogId: 'create_subscription_modal',
        defaultValues: opts
    });
}